import moment from "moment";
import { useTranslation } from "react-i18next";
import { formatNumber } from "../../../../utils/format-sub";
import getAvatarUrl from "../../../../utils/get-avatar-url";
import BandTag from "./BandTag";
import MemberRowActions, { isMsbUserId } from "./MemberRowActions";
import peopleNouns from "./peopleNouns";

const AtRiskTable = ({
  members = [],
  currency = "usd",
  context,
  theme = "dark",
  controls = null,
  // { push: bool, press: bool } — community DMs on for that product
  dmEnabled = {},
}) => {
  const { t } = useTranslation("coaching-portal");
  // All spans both products, so each row messages through its own.
  const chatType = (member) =>
    context === "all" ? String(member.product || "").toLowerCase() : context;

  return (
    <>
      <header className="table-header">
        <hgroup>
          <div className="flex items-center gap-2">
            <h3 className="title">
              {t("income.attention.atRiskTableTitle", {
                people: t(peopleNouns(context).plural),
              })}
            </h3>
            <BandTag kind="snapshot" label={t("income.bands.now")} />
          </div>
        </hgroup>
        {controls}
      </header>
      {/* biome-ignore lint/a11y/useValidAriaRole: report-table used for structure */}
      <table role="report-table" className="at-risk-table">
        {/* biome-ignore lint/a11y/useValidAriaRole: table-header used for structure */}
        <thead role="table-header">
          <tr>
            <th>{t(peopleNouns(context).column)}</th>
            <th>{t("income.attention.plan")}</th>
            <th>{t("income.attention.daysFailing")}</th>
            <th>{t("income.attention.amountDue")}</th>
            <th>{t("income.attention.attempts")}</th>
            <th>{t("income.attention.declineReason")}</th>
            <th>{t("income.attention.nextRetry")}</th>
            <th className="text-center">{t("income.attention.action")}</th>
          </tr>
        </thead>
        {members.length > 0 ? (
          <tbody>
            {members.map((member) => (
              <tr key={member.subscriptionId}>
                <td>
                  <div className="flex items-center gap-2">
                    <div className="image-wrapper">
                      {member.customerName ? (
                        <img
                          src={getAvatarUrl(
                            {
                              name: member.customerName,
                              avatar: member.avatar,
                            },
                            null,
                            theme
                          )}
                          className="w-8 h-8 rounded-full"
                          alt={t("income.avatarAlt")}
                        />
                      ) : (
                        <img
                          style={{ width: 32, height: 32, borderRadius: 32 }}
                          src="/img/avatars/registration-pending2.svg"
                          className="icon-grey"
                          alt=""
                        />
                      )}
                    </div>
                    <div className="flex flex-col">
                      <span className="subscriber-text">
                        {member.customerName ||
                          member.customerEmail ||
                          member.customerId}
                      </span>
                      {member.customerName && member.customerEmail && (
                        <span className="text-xs text-(--white-70)">
                          {member.customerEmail}
                        </span>
                      )}
                    </div>
                  </div>
                </td>
                <td>{member.planName || member.planId}</td>
                <td>
                  {member.daysFailing == null
                    ? "—"
                    : t("income.attention.daysFailingValue", {
                        count: member.daysFailing,
                      })}
                </td>
                <td className="text-(--danger)">
                  {formatNumber(member.amountDue, currency)}
                </td>
                <td>
                  {t("income.attention.attemptsValue", {
                    attempt: member.attemptCount,
                  })}
                </td>
                <td>
                  {/* No decline from Stripe means no card it could charge:
                      missing, expired or waiting on authentication. */}
                  {member.declineMessage ||
                    member.declineCode ||
                    t("income.attention.needsPaymentMethod")}
                </td>
                <td>
                  {member.nextPaymentAttempt
                    ? moment
                        .unix(member.nextPaymentAttempt)
                        .format("MMM D, YYYY")
                    : t("income.attention.noRetryScheduled")}
                </td>
                <td>
                  <MemberRowActions
                    member={member}
                    currency={currency}
                    chatType={chatType(member)}
                    canMessage={
                      dmEnabled[chatType(member)] &&
                      isMsbUserId(member.customerId)
                    }
                    t={t}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        ) : (
          <tbody>
            <tr>
              <td colSpan="100%">
                <div className="body-table-empty">
                  <p>{t("income.attention.noAtRisk")}</p>
                </div>
              </td>
            </tr>
          </tbody>
        )}
      </table>
    </>
  );
};

export default AtRiskTable;
